import { Button } from '@/components/ui/button';
import { Tag } from '@/components/ui/tag';
import { UnderlinedText } from '@/components/ui/underline-text';
import {
  FEATURE_CATEGORIES as CATEGORIES,
  type FeatureCategoryId,
  useFeaturesCategory,
} from '@/providers/features-category.provider';
import { MOTION } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { useLenis } from 'lenis/react';
import { useCallback, useRef } from 'react';
import { AiText, CheckItem, StoreBadgeLink } from './features-atoms';
import type { FeatureModalImage } from './features-image-modal';
import { FeatureSection } from './features-section';

type FeaturesContentProps = {
  onOpenImage: (image: FeatureModalImage) => void;
};

export default function FeaturesContent({ onOpenImage }: FeaturesContentProps) {
  const { activeCategory, setActiveCategory } = useFeaturesCategory();
  const isFirstRender = useRef(true);
  const categoryRevealTl = useRef<gsap.core.Timeline | null>(null);
  const lenis = useLenis();

  const sectionRef = useRef<HTMLElement>(null);
  const tagRef = useRef(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (
        !sectionRef.current ||
        !tagRef.current ||
        !titleRef.current ||
        !subtitleRef.current ||
        !tabsRef.current
      )
        return;

      const splitTitle = new SplitText(titleRef.current, { mask: 'lines', type: 'lines' });
      const splitSubtitle = new SplitText(subtitleRef.current, {
        mask: 'lines',
        type: 'lines',
      });

      gsap.set([tagRef.current, tabsRef.current], { autoAlpha: 0, y: 16 });
      gsap.set([splitTitle.lines, splitSubtitle.lines], {
        autoAlpha: 0,
        yPercent: MOTION.yPercent,
      });

      gsap
        .timeline({
          defaults: { duration: MOTION.durationMd, ease: MOTION.ease },
          scrollTrigger: {
            invalidateOnRefresh: true,
            start: 'top 60%',
            toggleActions: 'play none none reverse',
            trigger: sectionRef.current,
          },
        })
        .to(tagRef.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm })
        .to(
          [splitTitle.lines, splitSubtitle.lines],
          { stagger: MOTION.staggerLines, yPercent: 0, autoAlpha: 1 },
          '-=0.12',
        )
        .to(tabsRef.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm }, '-=0.5');

      // Sur mobile : la barre fixe n'a pas besoin d'offset vertical — ScrollTrigger
      // gère seul son apparition/disparition selon la présence de la section dans le viewport.
      const mm = gsap.matchMedia();
      mm.add('(max-width: 767px)', () => {
        gsap.set(tabsRef.current, { y: 0 });

        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: 'top bottom',
          end: 'bottom center',
          onToggle: ({ isActive }) => {
            gsap.to(tabsRef.current, {
              autoAlpha: isActive ? 1 : 0,
              y: 0,
              duration: isActive ? 0.3 : 0.2,
              overwrite: 'auto',
            });
          },
        });
      });

      return () => mm.revert();
    },
    { scope: sectionRef },
  );

  const switchCategory = useCallback(
    (next: FeatureCategoryId) => {
      if (next === activeCategory || !contentRef.current) return;

      if (lenis && sectionRef.current) {
        lenis.scrollTo(sectionRef.current, {
          duration: 0.6,
          offset: +195,
          easing: (t) => 1 - Math.pow(1 - t, 3),
        });
      }

      gsap.to(contentRef.current, {
        autoAlpha: 0,
        y: -10,
        duration: 0.2,
        ease: 'power2.in',
        onComplete: () => setActiveCategory(next),
      });
    },
    [activeCategory, lenis, setActiveCategory],
  );

  useGSAP(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!contentRef.current || !sectionRef.current) return;

    categoryRevealTl.current?.kill();

    const refreshScrollTriggers = () => {
      ScrollTrigger.refresh();
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(refreshScrollTriggers);
    });

    const blocks = Array.from(contentRef.current.querySelectorAll<HTMLElement>('[data-block]'));

    const tl = gsap.timeline({
      onComplete: refreshScrollTriggers,
    });
    categoryRevealTl.current = tl;

    tl.fromTo(
      contentRef.current,
      { autoAlpha: 0, y: 10 },
      { autoAlpha: 1, y: 0, duration: 0.35, ease: 'power2.out' },
      0.05,
    ).fromTo(
      blocks,
      { autoAlpha: 0, y: MOTION.y },
      {
        autoAlpha: 1,
        y: 0,
        stagger: 0.1,
        duration: MOTION.durationMd,
        ease: MOTION.ease,
      },
      0.15,
    );

    const images = Array.from(sectionRef.current.querySelectorAll('img'));
    void Promise.allSettled(
      images.map(async (image) => {
        if (image.complete) {
          await image.decode?.();
          return;
        }
        return new Promise<void>((resolve) => {
          const done = () => {
            image.removeEventListener('load', done);
            image.removeEventListener('error', done);
            resolve();
          };
          image.addEventListener('load', done, { once: true });
          image.addEventListener('error', done, { once: true });
        });
      }),
    ).then(() => {
      requestAnimationFrame(refreshScrollTriggers);
    });

    return () => {
      tl.kill();
    };
  }, [activeCategory]);

  return (
    <section
      ref={sectionRef}
      className="max-width-blue-section relative mx-auto flex flex-col items-start gap-12 px-6 py-16 pb-28 md:px-8 md:pb-16"
      id="fonctionnalites"
    >
      <div className="flex flex-col items-start gap-4">
        <Tag ref={tagRef}>Fonctionnalités</Tag>

        <div className="flex flex-col gap-2">
          <h2 ref={titleRef}>Toutes les fonctionnalités pour gérer votre cabinet</h2>

          <p ref={subtitleRef} aria-hidden="true" className="text-dark-blue max-w-2xl">
            Conçu avec et pour les avocats, zLawyer propose une solution complète, intuitive et
            adaptée à toutes les structures.
          </p>
          <p className="sr-only">
            Conçu avec et pour les avocats, zLawyer propose une solution complète, intuitive et
            adaptée à toutes les structures.
          </p>
        </div>
      </div>

      <div
        ref={tabsRef}
        className="border-dark-blue/10 fixed right-0 bottom-0 left-0 z-20 border-t bg-white px-4 py-3 md:sticky md:top-28 md:right-auto md:bottom-auto md:left-auto md:z-10 md:w-fit md:border-0 md:bg-transparent md:px-0 md:py-2"
      >
        <div className="border-dark-blue/8 flex w-full flex-wrap justify-center gap-1.5 rounded-2xl border bg-white p-1.5 shadow-[0_10px_40px_-12px_rgba(57,68,84,0.18)] md:justify-start">
          {CATEGORIES.map((categorie) => (
            <button
              key={categorie.id}
              type="button"
              className={clsx(
                'font-roboto-medium cursor-pointer rounded-xl px-5 py-2.5 text-sm transition-all duration-200',
                activeCategory === categorie.id
                  ? 'bg-orange text-white shadow-[0_4px_12px_rgba(237,99,15,0.25)]'
                  : 'text-dark-blue/70 hover:bg-orange/5 hover:text-orange',
              )}
              onClick={() => switchCategory(categorie.id)}
            >
              {categorie.label}
            </button>
          ))}
        </div>
      </div>

      <div ref={contentRef} className="flex flex-col gap-16 md:gap-24">
        {activeCategory === 'facturation' && (
          <>
            <div data-block>
              <FeatureSection
                imagePosition="right"
                image={{
                  alt: 'illustration de la fonctionnalité facturation',
                  height: 519,
                  src: withBasePath('/images/features/features9.webp'),
                  width: 863,
                }}
                title={
                  <>
                    <UnderlinedText>Facturez simplement.</UnderlinedText> Pilotez précisément
                  </>
                }
                onOpenImage={onOpenImage}
              >
                <p>zLawyer sécurise votre facturation et optimise votre gestion financière.</p>
                <ul className="flex flex-col gap-1">
                  <CheckItem>Facturation au temps passé ou au forfait</CheckItem>
                  <CheckItem>Gestion des frais et débours</CheckItem>
                  <CheckItem>Tarifs personnalisables</CheckItem>
                  <CheckItem>Majorations et minorations</CheckItem>
                  <CheckItem>Suivi financier centralisé</CheckItem>
                  <CheckItem>Relance des impayés simplifiée</CheckItem>
                </ul>
                <p className="mt-2">
                  Une facturation fluide, rapide et adaptée aux exigences des cabinets
                  d&apos;avocats.
                </p>
                <Button className="mt-4 w-fit" href="/contact/" variant="transparent">
                  Je souhaite une démonstration
                </Button>
              </FeatureSection>
            </div>

            <div id="facturation-electronique" data-block>
              <FeatureSection
                image={{
                  alt: 'illustration de la fonctionnalité facturation électronique',
                  height: 519,
                  src: withBasePath('/images/features/features10.webp'),
                  width: 863,
                }}
                title={
                  <span className="text-pretty">
                    <UnderlinedText>Facturation électronique</UnderlinedText> conforme
                  </span>
                }
                onOpenImage={onOpenImage}
              >
                <p>
                  <span className="font-bold">Anticipez la réforme en toute sérénité.</span>
                </p>
                <p>
                  Notre solution répond aux exigences de la facturation électronique et
                  s&apos;inscrit dans le cadre des obligations réglementaires en vigueur.
                </p>
                <ul className="flex flex-col gap-1">
                  <CheckItem>Conformité aux obligations légales</CheckItem>
                  <CheckItem>Aucun changement d&apos;outil ou d&apos;organisation</CheckItem>
                  <CheckItem>Gestion centralisée de vos dossiers et de votre facturation</CheckItem>
                  <CheckItem>Interface unique, simple et intuitive</CheckItem>
                </ul>
                <p>Une transition fluide, sans rupture dans votre organisation.</p>
                <div className="mt-4 flex flex-wrap items-center justify-start gap-4">
                  <Button href="/facturation-electronique-et-zlawyer" variant="fill">
                    Notre solution à la réforme
                  </Button>
                  <Button href="/facturation-electronique" variant="transparent">
                    Comprendre la réforme
                  </Button>
                </div>
              </FeatureSection>
            </div>

            <div data-block>
              <FeatureSection
                imagePosition="right"
                image={{
                  alt: 'illustration de la fonctionnalité rapprochement bancaire',
                  height: 519,
                  src: withBasePath('/images/features/features12.webp'),
                  width: 863,
                }}
                title={
                  <>
                    <UnderlinedText>Rapprochement bancaire</UnderlinedText> automatisé
                  </>
                }
                onOpenImage={onOpenImage}
              >
                <p>
                  Automatisez votre rapprochement bancaire et gardez une vision claire de votre
                  trésorerie.
                </p>
                <ul className="flex flex-col gap-1">
                  <CheckItem>Synchronisation bancaire</CheckItem>
                  <CheckItem>Lettrage intelligent des factures</CheckItem>
                  <CheckItem>Suivi en temps réel</CheckItem>
                  <CheckItem>Zéro saisie manuelle</CheckItem>
                </ul>
                <p>Gagnez du temps et fiabilisez votre trésorerie.</p>
                <Button className="mt-4 w-fit" href="/contact/" variant="transparent">
                  Je souhaite une démonstration
                </Button>
              </FeatureSection>
            </div>
          </>
        )}

        {activeCategory === 'gestion' && (
          <>
            <div data-block>
              <FeatureSection
                image={{
                  alt: 'illustration de la fonctionnalité pilotez votre activité',
                  height: 539,
                  src: withBasePath('/images/features/features4.webp'),
                  width: 863,
                }}
                title={
                  <>
                    <UnderlinedText>Pilotez votre activité</UnderlinedText> en toute simplicité
                  </>
                }
                onOpenImage={onOpenImage}
              >
                <p>
                  Centralisez vos informations, suivez votre activité et valorisez chaque minute.
                </p>
                <p>
                  <span className="font-bold">Vos dossiers, immédiatement accessibles.</span>{' '}
                  Retrouvez en un instant toutes les informations essentielles de vos dossiers,
                  centralisées dans une interface claire et intuitive.{' '}
                  <span className="font-bold">Moins de recherche, plus d&apos;efficacité.</span>
                </p>
                <p>
                  <span className="font-bold">Chaque minute devient facturable.</span> Transformez
                  facilement votre temps passé sur chaque dossier en facturation fiable et
                  maîtrisée.
                </p>
                <Button className="mt-4 w-fit" href="/contact/" variant="transparent">
                  Je souhaite une démonstration
                </Button>
              </FeatureSection>
            </div>

            <div data-block>
              <FeatureSection
                imagePosition="right"
                image={{
                  alt: "illustration de la fonctionnalité essentielle de l'application",
                  height: 519,
                  src: withBasePath('/images/features/features11.webp'),
                  width: 863,
                }}
                title={
                  <>
                    <UnderlinedText>MYzLawyer</UnderlinedText> : votre tableau de bord
                  </>
                }
                onOpenImage={onOpenImage}
              >
                <p>
                  Ce{' '}
                  <span className="font-bold">
                    tableau de bord entièrement personnalisable pour avocats
                  </span>{' '}
                  centralise les informations essentielles de votre activité.
                </p>
                <p>Dès votre connexion, visualisez en un coup d&apos;oeil :</p>
                <ul className="flex flex-col gap-1">
                  <CheckItem>Échéances et tâches</CheckItem>
                  <CheckItem>Dossiers à facturer</CheckItem>
                  <CheckItem>Factures à relancer</CheckItem>
                  <CheckItem>Indicateurs clés</CheckItem>
                </ul>
                <p>Un pilotage simple, clair et efficace.</p>
                <Button className="mt-4 w-fit" href="/contact/" variant="transparent">
                  Je souhaite une démonstration
                </Button>
              </FeatureSection>
            </div>

            <div data-block>
              <FeatureSection
                title={<UnderlinedText>Suivi des performances</UnderlinedText>}
                image={{
                  alt: 'illustration de la fonctionnalité suivi des performances',
                  height: 519,
                  src: withBasePath('/images/features/features1.webp'),
                  width: 863,
                }}
                onOpenImage={onOpenImage}
              >
                <p>
                  <span className="font-bold">Pilotez votre cabinet avec précision.</span>
                </p>
                <p>
                  Avec zLawyer, gardez le contrôle sur la performance financière de votre cabinet
                  d&apos;avocats. Accédez à des indicateurs clés et des rapports détaillés pour
                  suivre l&apos;activité de chaque dossier et comprendre la rentabilité de votre
                  cabinet.
                </p>
                <ul className="flex flex-col gap-1">
                  <CheckItem>Analyse de rentabilité</CheckItem>
                  <CheckItem>Suivi des dossiers</CheckItem>
                  <CheckItem>Indicateurs clés</CheckItem>
                  <CheckItem>Aide à la décision</CheckItem>
                </ul>
                <p>Transformez vos données en décisions concrètes.</p>
                <Button className="mt-4 w-fit" href="/contact/" variant="transparent">
                  Je souhaite une démonstration
                </Button>
              </FeatureSection>
            </div>

            <div data-block>
              <FeatureSection
                imagePosition="right"
                image={{
                  alt: 'illustration de la fonctionnalité gestion des tâches',
                  height: 519,
                  src: withBasePath('/images/features/features14.webp'),
                  width: 863,
                }}
                title={
                  <>
                    <UnderlinedText>Gestion des tâches</UnderlinedText> pour cabinets d&apos;avocats
                  </>
                }
                onOpenImage={onOpenImage}
              >
                <p>
                  <span className="font-bold">Maîtrisez votre organisation. Gagnez en clarté.</span>
                </p>
                <p>
                  Avec zLawyer, centralisez l&apos;ensemble de vos tâches dans un environnement
                  unique pensé pour les avocats. Accédez instantanément à une vision claire de votre
                  activité : tâches à faire, en cours, terminées, priorités et urgences.
                </p>
                <p>
                  Identifiez en un instant ce qui nécessite votre attention, par dossier ou par
                  intervenant, et pilotez votre travail avec méthode et précision.
                </p>
                <p>
                  <span className="font-bold">
                    Une organisation pensée pour la performance des cabinets
                  </span>
                </p>
                <ul className="flex flex-col gap-1">
                  <CheckItem>Une vision claire pour mieux décider</CheckItem>
                  <CheckItem>Une coordination fluide entre les équipes</CheckItem>
                  <CheckItem>Une gestion du temps optimisée</CheckItem>
                  <CheckItem>Une productivité renforcée sans complexité</CheckItem>
                </ul>
                <p>
                  Concentrez-vous sur l&apos;essentiel : vos dossiers, vos clients, votre expertise.
                  zLawyer s&apos;occupe de l&apos;organisation.
                </p>
                <Button className="mt-4 w-fit" href="/contact/" variant="transparent">
                  Je souhaite une démonstration
                </Button>
              </FeatureSection>
            </div>

            <div data-block>
              <FeatureSection
                image={{
                  alt: 'illustration de la fonctionnalité LCB-FT',
                  height: 519,
                  src: withBasePath('/images/features/features15.webp'),
                  width: 863,
                }}
                title={
                  <>
                    <UnderlinedText>LCB-FT</UnderlinedText> : conformité anti-blanchiment
                  </>
                }
                onOpenImage={onOpenImage}
              >
                <p>
                  <span className="font-bold">
                    Respectez vos obligations réglementaires sans effort.
                  </span>
                </p>
                <p>
                  zLawyer intègre un module dédié à la lutte contre le blanchiment de capitaux et le
                  financement du terrorisme, conçu pour répondre aux exigences légales des cabinets
                  d&apos;avocats.
                </p>
                <ul className="flex flex-col gap-1">
                  <CheckItem>Identification et vérification des clients</CheckItem>
                  <CheckItem>Classification des risques par dossier</CheckItem>
                  <CheckItem>Traçabilité des opérations</CheckItem>
                  <CheckItem>Alertes et suivi des obligations déclaratives</CheckItem>
                </ul>
                <p>Sécurisez votre cabinet et restez en conformité en toute sérénité.</p>
                <Button className="mt-4 w-fit" href="/contact/" variant="transparent">
                  Je souhaite une démonstration
                </Button>
              </FeatureSection>
            </div>
          </>
        )}

        {activeCategory === 'documents' && (
          <>
            <div data-block>
              <FeatureSection
                image={{
                  alt: 'illustration de la fonctionnalité gestion documentaire',
                  height: 481,
                  src: withBasePath('/images/features/features3.webp'),
                  width: 863,
                }}
                title={
                  <>
                    <UnderlinedText>Gestion documentaire</UnderlinedText> sans contrainte
                  </>
                }
                onOpenImage={onOpenImage}
              >
                <p>
                  zLawyer simplifie l&apos;organisation de vos documents tout en respectant vos
                  exigences de confidentialité.
                </p>
                <p>Vos documents restent sur votre environnement (OneDrive, SharePoint...)</p>
                <ul className="flex flex-col gap-1">
                  <CheckItem>Organisation simple par dossier</CheckItem>
                  <CheckItem>Accès immédiat</CheckItem>
                  <CheckItem>Intégration fluide</CheckItem>
                </ul>
                <p>
                  <span className="font-bold">
                    Aucune migration. Aucun surcoût. 100 % maîtrise de vos données.
                  </span>
                </p>
                <Button className="mt-4 w-fit" href="/contact/" variant="transparent">
                  Je souhaite une démonstration
                </Button>
              </FeatureSection>
            </div>

            <div data-block>
              <FeatureSection
                imagePosition="right"
                image={{
                  alt: 'illustration de la fonctionnalité accès client',
                  height: 519,
                  src: withBasePath('/images/features/features8.webp'),
                  width: 863,
                }}
                title={
                  <>
                    Donnez à vos clients <UnderlinedText>un accès à leur dossier</UnderlinedText>{' '}
                    sur le web
                  </>
                }
                onOpenImage={onOpenImage}
              >
                <p>
                  Ce service vous permet de disposer d&apos;un espace à vos couleurs sur le Web afin
                  de permettre aux clients de votre choix d&apos;accéder à certaines informations de
                  leurs dossiers.
                </p>
                <Button className="mt-4 w-fit" href="/contact/" variant="transparent">
                  Je souhaite une démonstration
                </Button>
              </FeatureSection>
            </div>

            <div data-block>
              <FeatureSection
                image={{
                  alt: 'illustration de la fonctionnalité tamponnage numérique',
                  height: 519,
                  src: withBasePath('/images/features/features13.webp'),
                  width: 863,
                }}
                title={
                  <>
                    <UnderlinedText>Tamponnage numérique</UnderlinedText> des pièces
                  </>
                }
                onOpenImage={onOpenImage}
              >
                <p>
                  Simplifiez la préparation de vos dossiers de plaidoirie grâce au{' '}
                  <span className="font-bold">tamponnage numérique intégré à zLawyer.</span>
                </p>
                <p>
                  Vos pièces sont automatiquement numérotées et organisées pour garantir des
                  dossiers clairs, structurés et conformes aux pratiques des juridictions.
                </p>
                <ul className="flex flex-col gap-1">
                  <CheckItem>Numérotation automatique</CheckItem>
                  <CheckItem>Dossiers structurés</CheckItem>
                  <CheckItem>Gain de temps</CheckItem>
                  <CheckItem>Fiabilité renforcée</CheckItem>
                </ul>
                <p>Préparez vos dossiers plus rapidement et plaidez en toute sérénité.</p>
                <Button className="mt-4 w-fit" href="/contact/" variant="transparent">
                  Je souhaite une démonstration
                </Button>
              </FeatureSection>
            </div>
          </>
        )}

        {activeCategory === 'innovation' && (
          <>
            <div data-block>
              <FeatureSection
                imagePosition="right"
                image={{
                  alt: 'illustration de la fonctionnalité IA Zéphyr',
                  height: 539,
                  src: withBasePath('/images/features/features7.webp'),
                  width: 863,
                }}
                title={
                  <>
                    L&apos;<AiText>IA</AiText> au service de votre expertise
                  </>
                }
                onOpenImage={onOpenImage}
              >
                <p>
                  Avec <span className="font-bold">Zéphyr</span>, accédez à une IA générative conçue
                  pour les avocats.
                </p>
                <ul className="flex flex-col gap-1">
                  <CheckItem>Recherche</CheckItem>
                  <CheckItem>Rédaction</CheckItem>
                  <CheckItem>Analyse</CheckItem>
                </ul>
                <p>Gagnez en rapidité et en efficacité au quotidien.</p>
                <Button className="mt-4 w-fit" href="/contact/" variant="transparent">
                  Je souhaite une démonstration
                </Button>
              </FeatureSection>
            </div>

            <div data-block>
              <FeatureSection
                imageClassName="object-contain md:h-96 lg:w-fit"
                imageFramed={false}
                image={{
                  alt: "illustration de l'application mobile",
                  height: 647,
                  src: withBasePath('/images/features/features2.webp'),
                  width: 863,
                }}
                title={
                  <>
                    <UnderlinedText>Application mobile</UnderlinedText> zLawyer
                  </>
                }
                onOpenImage={onOpenImage}
              >
                <p>
                  <span className="font-bold">Votre cabinet, partout avec vous.</span>
                </p>
                <ul className="flex flex-col gap-1">
                  <CheckItem>Saisie des temps en mobilité</CheckItem>
                  <CheckItem>Consultation hors ligne</CheckItem>
                  <CheckItem>Dossiers toujours disponibles</CheckItem>
                  <CheckItem>Indicateurs clés à portée de main</CheckItem>
                </ul>
                <p>Ne perdez plus aucune information, où que vous soyez.</p>
                <div className="flex flex-wrap">
                  <StoreBadgeLink
                    alt="logo de téléchargement vers l'Apple Store"
                    href="https://apps.apple.com/fr/app/zlawyer/id1174916635?mt=8"
                    src={withBasePath('/images/logos/badgeAppStore.svg')}
                  />
                  <StoreBadgeLink
                    alt="logo de téléchargement vers le Play Store"
                    href="https://play.google.com/store/apps/details?id=com.zelesolution.zlawyer&hl=fr&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1"
                    src={withBasePath('/images/logos/badgePlayStore.svg')}
                  />
                </div>
              </FeatureSection>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
